/**
 * CancelSubscriptionFlow — the V2 Settings → Manage Subscription funnel.
 *
 * Replaces the old "email support@vivideo.ai and we cancel it by hand" screen
 * with a real, self-service flow. The entry button says "Manage subscription",
 * not "Cancel", so the first window is a hub:
 *
 *   0. menu       — plan summary + two paths: Upgrade plan (→ /subscription)
 *                   or Cancel subscription (→ the funnel below)
 *   1. reason     — why are you leaving? (8 single-choice reasons)
 *   1b. learn     — prompting + model education with a "Made with Vivideo"
 *                   showcase video (owner: don't let them cancel uninformed)
 *   2. confirm    — "are you sure": days left + what you lose
 *   3. pause      — pause billing for 1 week instead (weekly/monthly subs);
 *                   rejecting it arms a PERMANENTLY-deleted acknowledgement
 *   4. use-credits — advisory: spend your credits + finish projects first
 *   5. bonus      — 20 free credits if you stay (once per user, ever)
 *   6. final      — feedback textarea (REQUIRED) + acknowledgement + a 10s
 *                   countdown inside the cancel button
 *   7. type-confirm — type the confirm word to unlock the REAL cancel button
 *   8. Stripe     — the hosted cancel page (billing portal), or an in-app
 *                   confirmation when the portal has no cancel flow
 *
 * Friction (owner): every funnel window has a 5s reading-gate bar (except
 * final, which has the countdown), and the cancel-direction button only
 * renders after scrolling to the bottom (bounce-arrow hint).
 *
 * Steps 3 and 4 self-skip when the user does not qualify (yearly sub for
 * pause; any previously used retention offer blocks both). Numbers on screen —
 * renewal date, days left, credits, asset counts — come from the
 * v2-cancel-subscription lambda, with a user-store fallback when that call
 * fails (endpoint not deployed yet / transient outage).
 *
 * Layout mirrors the reference flow (split modal: copy left, artwork right,
 * two footer buttons with the retention action on the right); the palette and
 * button styling are Vivideo's.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  X, Check, Info, Loader2, Gem, FolderOpen, Wand2, Film, Sparkles,
  Layers, Users, PlayCircle, Star, Pause, Gift, CalendarClock,
  ArrowUpRight, XCircle, ChevronRight, ChevronDown, CreditCard, AlertTriangle, Maximize2, Lightbulb,
} from 'lucide-react';
import { cn } from '@/v2/utils';
import { apiService } from '@/api/apiService';
import { useUserStore } from '@/store/userStore';
import { CREDIT_COST } from '@/api/config';
import { toast } from '@/v2/toast';
import { trackAmpRetentionOfferAccepted } from '@/services/amplitudeService';
import type { CancelFlowInfo } from '@/api/v2Types';

type Step = 'menu' | 'reason' | 'learn' | 'confirm' | 'pause' | 'use-credits' | 'bonus' | 'final' | 'type-confirm' | 'retention-done' | 'done';

/** The 8 reasons, in the reference order. Ids match the lambda's Stripe map. */
const REASONS = [
  'not_needed',
  'too_expensive',
  'dont_know_how',
  'result_issue',
  'billing_issue',
  'missing_feature',
  'switching',
  'other',
] as const;
type Reason = (typeof REASONS)[number];

// ---------------------------------------------------------------------------
// Artwork — the right-hand panel. Built from gradients + icons (no binaries),
// one scene per step so every window has its own illustration.
// ---------------------------------------------------------------------------

/** Muted, looping showcase player: click to play/pause, fullscreen button,
 *  progress bar, and the "Made with Vivideo" badge. No audio track. */
function VideoShowcase({ src, badge }: { src: string; badge: string }) {
  const { t } = useTranslation('v2');
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Start as NOT playing: if iOS blocks autoplay (Low Power Mode etc.) the
  // tap-to-play overlay must be visible — with `true` the video sat frozen
  // with no affordance at all.
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // iOS Safari checks the muted ATTRIBUTE when deciding autoplay
    // eligibility, and React only sets the property — set everything
    // imperatively, then try to start. A rejected play() just leaves the
    // overlay up; the user's tap (a real gesture) starts it.
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('muted', '');
    const attempt = v.play();
    if (attempt) attempt.catch(() => setPlaying(false));
  }, []);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => setPlaying(false)); } else { v.pause(); }
  };

  const goFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation();
    const el = wrapRef.current as (HTMLDivElement & { webkitRequestFullscreen?: () => void }) | null;
    const v = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    if (!el) return;
    if (document.fullscreenElement) { void document.exitFullscreen(); return; }
    if (el.requestFullscreen) { void el.requestFullscreen(); }
    else if (v?.webkitEnterFullscreen) {
      // iPhone Safari: no element fullscreen at all — only the native video
      // fullscreen presentation exists.
      v.webkitEnterFullscreen();
    } else {
      el.webkitRequestFullscreen?.();
    }
  };

  return (
    <div
      ref={wrapRef}
      onClick={toggle}
      className="group relative cursor-pointer overflow-hidden rounded-2xl border border-glass-border bg-black"
    >
      <video
        ref={videoRef}
        src={src}
        autoPlay
        muted
        loop
        playsInline
        onPlay={() => setPlaying(true)}
        onPlaying={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          if (v.duration) setProgress(v.currentTime / v.duration);
        }}
        className="block h-auto w-full"
      />
      <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 backdrop-blur-sm">
        <Sparkles className="h-3.5 w-3.5 text-white" />
        <span className="text-xs font-semibold text-white">{badge}</span>
      </div>
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <PlayCircle className="h-14 w-14 text-white/90" />
        </div>
      )}
      <button
        type="button"
        onClick={goFullscreen}
        aria-label={t('cancelFlow.fullscreen')}
        className="absolute bottom-2.5 right-2.5 rounded-lg bg-black/60 p-2 text-white/80 backdrop-blur-sm transition-opacity hover:text-white"
      >
        <Maximize2 className="h-4 w-4" />
      </button>
      <div className="absolute inset-x-0 bottom-0 h-1 bg-white/20">
        <div className="h-full bg-gradient-to-r from-primary to-accent" style={{ width: `${progress * 100}%` }} />
      </div>
    </div>
  );
}

function FloatCard({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div className={cn(
      'absolute rounded-2xl border border-white/25 bg-white/15 px-3 py-2.5 backdrop-blur-md shadow-xl',
      className
    )}>{children}</div>
  );
}

function ArtPanel({ step, credits, bonusCredits }: { step: Step; credits: number; bonusCredits: number }) {
  const { t } = useTranslation('v2');

  return (
    <div className="relative hidden md:block w-[38%] shrink-0 overflow-hidden">
      {/* Base wash — one gradient family per step so the flow reads as a journey */}
      <div className={cn(
        'absolute inset-0',
        (step === 'menu' || step === 'reason' || step === 'learn') && 'bg-gradient-to-br from-[#6D5BD0] via-[#8B5CF6] to-[#C084FC]',
        step === 'confirm' && 'bg-gradient-to-br from-[#7C3AED] via-[#8B5CF6] to-[#A5B4FC]',
        step === 'pause' && 'bg-gradient-to-br from-[#4F46E5] via-[#7C3AED] to-[#38BDF8]',
        step === 'use-credits' && 'bg-gradient-to-br from-[#92400E] via-[#B45309] to-[#7C3AED]',
        step === 'bonus' && 'bg-gradient-to-br from-[#312E81] via-[#6D28D9] to-[#0F172A]',
        (step === 'final' || step === 'type-confirm' || step === 'done' || step === 'retention-done') && 'bg-gradient-to-br from-[#1E1B4B] via-[#5B21B6] to-[#312E81]',
      )} />
      {/* Soft light blooms */}
      <div className="absolute -top-16 -right-10 h-56 w-56 rounded-full bg-white/25 blur-3xl" />
      <div className="absolute bottom-0 -left-12 h-48 w-48 rounded-full bg-white/15 blur-3xl" />

      {/* Starfield for the darker, late-funnel scenes */}
      {(step === 'bonus' || step === 'final' || step === 'type-confirm' || step === 'done' || step === 'retention-done') && (
        <div className="absolute inset-0">
          {[[18, 22], [72, 14], [40, 9], [86, 38], [12, 58], [64, 70], [30, 84], [90, 76]].map(([left, top], i) => (
            <Star
              key={i}
              className="absolute text-white/70"
              style={{ left: `${left}%`, top: `${top}%`, width: i % 3 === 0 ? 14 : 8, height: i % 3 === 0 ? 14 : 8 }}
              fill="currentColor"
            />
          ))}
        </div>
      )}

      <div className="relative h-full w-full">
        {(step === 'menu' || step === 'reason' || step === 'learn') && (
          <>
            <FloatCard className="left-6 top-16 -rotate-6">
              <div className="flex items-center gap-2">
                <PlayCircle className="h-5 w-5 text-white" />
                <span className="text-xs font-semibold text-white">{t('cancelFlow.artNowPlaying')}</span>
              </div>
              <div className="mt-2 h-1.5 w-28 rounded-full bg-white/30">
                <div className="h-full w-1/3 rounded-full bg-white" />
              </div>
            </FloatCard>
            <FloatCard className="right-5 top-40 rotate-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-white" />
                <span className="text-xs font-semibold text-white">{t('cancelFlow.artModels')}</span>
              </div>
            </FloatCard>
            <div className="absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-white/30 bg-white/20 backdrop-blur-md shadow-2xl flex items-center justify-center">
              <Film className="h-16 w-16 text-white/90" />
            </div>
            <FloatCard className="bottom-16 left-10 rotate-2">
              <div className="flex items-center gap-2">
                <Gem className="h-4 w-4 text-white" />
                <span className="text-xs font-semibold text-white">{credits} {t('cancelFlow.artCredits')}</span>
              </div>
            </FloatCard>
          </>
        )}

        {step === 'confirm' && (
          <>
            {/* Mirrors the reference: the things you are about to give up,
                floating as glass chips around the plan. */}
            <FloatCard className="left-6 top-10 -rotate-3 w-44">
              <div className="flex items-center gap-2">
                <Gem className="h-4 w-4 text-white" />
                <span className="text-xs font-semibold text-white">{t('cancelFlow.assetCreditsTitle', { credits })}</span>
              </div>
              <div className="mt-2 h-1.5 w-full rounded-full bg-white/30">
                <div className="h-full w-2/3 rounded-full bg-white" />
              </div>
            </FloatCard>
            <FloatCard className="right-4 top-32 rotate-3">
              <div className="flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-white" />
                <span className="text-xs font-semibold text-white">{t('cancelFlow.artPremiumTools')}</span>
              </div>
            </FloatCard>
            <FloatCard className="left-4 top-1/2 -rotate-2">
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-white" />
                <span className="text-xs font-semibold text-white">{t('cancelFlow.artModels')}</span>
              </div>
            </FloatCard>
            <FloatCard className="right-6 bottom-32 rotate-2">
              <div className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-white" />
                <span className="text-xs font-semibold text-white">{t('cancelFlow.artSavedAssets')}</span>
              </div>
            </FloatCard>
            <FloatCard className="left-10 bottom-14 rotate-1">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-white" />
                <span className="text-xs font-semibold text-white">{t('cancelFlow.artSharedProjects')}</span>
              </div>
            </FloatCard>
          </>
        )}

        {step === 'pause' && (
          <>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="flex h-40 w-40 items-center justify-center rounded-3xl border border-white/30 bg-white/20 backdrop-blur-md shadow-2xl">
                <Pause className="h-20 w-20 text-white" fill="currentColor" />
              </div>
            </div>
            <FloatCard className="right-6 top-24 rotate-3">
              <div className="flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-white" />
                <span className="text-xs font-semibold text-white">{t('cancelFlow.artOneWeek')}</span>
              </div>
            </FloatCard>
            <FloatCard className="bottom-20 left-8 -rotate-2">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-white" />
                <span className="text-xs font-semibold text-white">{t('cancelFlow.artNoCharges')}</span>
              </div>
            </FloatCard>
          </>
        )}

        {step === 'use-credits' && (
          <>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="flex h-40 w-40 items-center justify-center rounded-3xl border border-white/30 bg-white/20 backdrop-blur-md shadow-2xl">
                <Gem className="h-20 w-20 text-white" />
              </div>
            </div>
            <FloatCard className="right-6 top-24 rotate-3">
              <div className="flex items-center gap-2">
                <Gem className="h-4 w-4 text-white" />
                <span className="text-xs font-semibold text-white">{credits} {t('cancelFlow.artCredits')}</span>
              </div>
            </FloatCard>
            <FloatCard className="bottom-20 left-8 -rotate-2">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-white" />
                <span className="text-xs font-semibold text-white">{t('cancelFlow.artPaidFor')}</span>
              </div>
            </FloatCard>
          </>
        )}

        {step === 'bonus' && (
          <>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="relative flex h-40 w-40 items-center justify-center rounded-3xl border border-white/30 bg-white/15 backdrop-blur-md shadow-2xl">
                <Gift className="h-20 w-20 text-white" />
                <Sparkles className="absolute -right-4 -top-4 h-9 w-9 text-white" />
              </div>
            </div>
            <FloatCard className="right-8 bottom-24 rotate-2">
              <div className="flex items-center gap-2">
                <Gem className="h-4 w-4 text-white" />
                <span className="text-xs font-semibold text-white">+{bonusCredits} {t('cancelFlow.artCredits')}</span>
              </div>
            </FloatCard>
          </>
        )}

        {(step === 'final' || step === 'type-confirm' || step === 'done' || step === 'retention-done') && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
            <div className="mx-auto flex h-40 w-40 items-center justify-center rounded-3xl border border-white/30 bg-white/15 backdrop-blur-md shadow-2xl">
              {step === 'final' || step === 'type-confirm'
                ? <Film className="h-20 w-20 text-white" />
                : <Check className="h-20 w-20 text-white" />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small shared bits
// ---------------------------------------------------------------------------

function Radio({ checked }: { checked: boolean }) {
  return (
    <span className={cn(
      'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
      checked ? 'border-primary' : 'border-text-tertiary'
    )}>
      {checked && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
    </span>
  );
}

// ---------------------------------------------------------------------------
// The funnel
// ---------------------------------------------------------------------------

export function CancelSubscriptionFlow({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation('v2');
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const userId = useUserStore((s) => s.userId);
  const refreshUser = useUserStore((s) => s.refreshUser);

  // The entry button says "Manage subscription", so the flow opens on a hub
  // (upgrade vs cancel) — never straight into the cancellation funnel.
  const [step, setStep] = useState<Step>('menu');
  const [info, setInfo] = useState<CancelFlowInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState<Reason | null>(null);
  const [details, setDetails] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [retentionKind, setRetentionKind] = useState<'pause' | 'bonus' | null>(null);
  const [resumesAt, setResumesAt] = useState<number | null>(null);
  const [canceledUntil, setCanceledUntil] = useState<number | null>(null);
  // Guards money-moving submits against double-clicks: setState is async, so
  // several clicks in one tick would all read the old `busy`. The ref flips
  // synchronously, so exactly one request goes out.
  const inFlight = useRef(false);

  // One id per funnel run, so every survey/cancel call updates the SAME row.
  const flowIdRef = useRef<string | null>(null);
  if (!flowIdRef.current) {
    flowIdRef.current = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  const flowId = flowIdRef.current;

  const planLabel = info?.planLabel ?? (user?.userPlan === 'max' ? 'MAX' : 'PRO');
  const credits = info?.credits ?? user?.remainingCredit ?? 0;
  const creditsPerVideo = info?.creditsPerVideo || CREDIT_COST;
  const bonusCredits = info?.bonus?.credits ?? 20;
  const pauseDays = info?.pause?.days ?? 7;

  // ---- load the live snapshot -------------------------------------------
  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    let cancelled = false;
    apiService.cancelFlowInfo(userId)
      .then((data) => {
        if (cancelled) return;
        setInfo(data);
        // Already scheduled to cancel: keep the menu (they may want to
        // upgrade!) — the Cancel option then shows the status screen instead
        // of re-running the funnel.
        if (data.alreadyCanceling) setCanceledUntil(data.periodEnd);
      })
      // A failed snapshot must not trap the user: the funnel still runs on
      // store data (see the `info == null` fallbacks below).
      .catch(() => { if (!cancelled) setInfo(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  const survey = useCallback((nextStep: string, outcome?: string) => {
    if (!userId) return;
    void apiService.cancelFlowSurvey(userId, {
      flowId, step: nextStep, reason, details, outcome,
    }).catch(() => { /* analytics only — never blocks the funnel */ });
  }, [userId, flowId, reason, details]);

  // ---- step machine ------------------------------------------------------
  // When the info snapshot is unavailable (endpoint not yet deployed, or a
  // transient outage), fall back to the user store: the offers still show for
  // anyone who hasn't used a retention offer, and the claim itself stays safe —
  // the backend re-checks eligibility atomically and just errors politely.
  // Pause never applies to yearly subs (paid upfront), with or without info.
  const hasPause = info
    ? info.pause.eligible
    : !user?.retentionOfferUsed && user?.priceType !== 'yearly';
  const hasBonus = info ? info.bonus.eligible : !user?.retentionOfferUsed;

  const goNext = (from: Step) => {
    const order: Step[] = ['reason', 'learn', 'confirm', 'pause', 'use-credits', 'bonus', 'final', 'type-confirm'];
    let idx = order.indexOf(from) + 1;
    while (idx < order.length) {
      const candidate = order[idx];
      if (candidate === 'pause' && !hasPause) { idx += 1; continue; }
      // Nothing to spend = nothing to advise; skip the use-credits warning.
      if (candidate === 'use-credits' && credits <= 0) { idx += 1; continue; }
      if (candidate === 'bonus' && !hasBonus) { idx += 1; continue; }
      setStep(candidate);
      survey(candidate);
      return;
    }
    setStep('final');
    survey('final');
  };

  const keepPlan = (outcome: string) => {
    survey(step, outcome);
    onClose();
  };

  // ---- 5-second reading gate --------------------------------------------
  // Every cancellation window disables its cancel-direction button for 5s
  // behind a filling bar (owner: make sure the info is read + add friction).
  // The stay-direction buttons (keep plan, pause, claim credits) are NEVER
  // gated — friction must push toward staying, not against the offers.
  const GATED_STEPS: Step[] = ['reason', 'learn', 'confirm', 'pause', 'use-credits', 'bonus'];
  const isGatedStep = GATED_STEPS.includes(step);
  const [gateDone, setGateDone] = useState(false);
  useEffect(() => {
    if (!isGatedStep || loading) { setGateDone(true); return; }
    setGateDone(false);
    const timer = window.setTimeout(() => setGateDone(true), 5000);
    return () => window.clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, loading]);

  // ---- scroll gate -------------------------------------------------------
  // The cancel-direction button only APPEARS once the user scrolls to the
  // bottom of the window (owner: open on the positive button only, with a
  // small arrow showing there is more). A dynamic bottom spacer guarantees
  // every gated window actually has something to scroll.
  const SCROLL_GATED: Step[] = ['reason', 'learn', 'confirm', 'pause', 'use-credits', 'bonus', 'final', 'type-confirm'];
  const isScrollGated = SCROLL_GATED.includes(step) && !loading;
  const [scrolledDown, setScrolledDown] = useState(false);
  const [spacerPx, setSpacerPx] = useState(0);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  // Swapping a window's content can make the browser fire a clamped scroll
  // event that would instantly "complete" the next window's scroll gate. The
  // gate therefore only listens once the new window has settled (next frame),
  // and only to positions that required a real user scroll.
  const scrollArmRef = useRef(false);
  useEffect(() => {
    scrollArmRef.current = false;
    setScrolledDown(!isScrollGated);
    setSpacerPx(0);
    const el = bodyRef.current;
    if (el) el.scrollTop = 0;
    if (!isScrollGated) return;
    const id = window.requestAnimationFrame(() => {
      const node = bodyRef.current;
      const content = contentRef.current;
      if (!node || !content) return;
      // Measure the CONTENT, not scrollHeight — scrollHeight clamps to the
      // container when the content is shorter, which under-sizes the spacer.
      const shortfall = node.clientHeight + 80 - content.offsetHeight;
      if (shortfall > 0) setSpacerPx(shortfall);
      scrollArmRef.current = true;
    });
    return () => window.cancelAnimationFrame(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, loading]);
  const onBodyScroll = () => {
    const el = bodyRef.current;
    if (!el || !scrollArmRef.current || scrolledDown) return;
    if (el.scrollTop > 40 && el.scrollTop + el.clientHeight >= el.scrollHeight - 12) setScrolledDown(true);
  };

  // ---- pause-rejection acknowledgement -----------------------------------
  // Rejecting the pause offer arms an amber PERMANENTLY-deleted checkbox in
  // the same window; the continue button turns red and stays disabled until
  // it is ticked.
  const [pauseRejectArmed, setPauseRejectArmed] = useState(false);
  const [pauseRejectAgreed, setPauseRejectAgreed] = useState(false);
  // Money-wasted warning on the use-credits window: the first continue press
  // arms it (flashy red box, button turns red), the second press proceeds.
  const [wasteArmed, setWasteArmed] = useState(false);
  // Type-to-confirm window: the exact word unlocks the real cancel button.
  const [typedConfirm, setTypedConfirm] = useState('');
  useEffect(() => {
    setPauseRejectArmed(false); setPauseRejectAgreed(false);
    setWasteArmed(false); setTypedConfirm('');
  }, [step]);

  // ---- money-wasted math --------------------------------------------------
  // What cancelling burns TODAY: unused credits at the standard top-up rate
  // ($2 per 10 credits) + the already-paid remainder of the billing period.
  const CREDIT_USD_RATE = 0.2;
  const INTERVAL_DAYS: Record<'weekly' | 'monthly' | 'yearly', number> = { weekly: 7, monthly: 30, yearly: 365 };
  const wasteCreditsValue = credits * CREDIT_USD_RATE;
  const wasteDaysValue = info
    ? info.amount * Math.min(1, info.daysLeft / INTERVAL_DAYS[info.interval])
    : 0;
  const wasteTotal = wasteCreditsValue + wasteDaysValue;

  const confirmWord = t('cancelFlow.typeConfirmWord');
  // Diacritic/case-insensitive match so every locale's confirm word works:
  // Turkish İ/ı, Greek tonos, etc. — "iptal" matches "İPTAL", "ακύρωση"
  // matches "ΑΚΥΡΩΣΗ". NFD + strip combining marks + uppercase.
  const normalizeConfirmWord = (value: string) =>
    value.trim().normalize('NFD').replace(/\p{M}/gu, '').toUpperCase();
  const typedOk = typedConfirm.trim().length > 0
    && normalizeConfirmWord(typedConfirm) === normalizeConfirmWord(confirmWord);

  // ---- final-window countdown --------------------------------------------
  // 10 seconds inside the cancel button itself (no 3s bar on this window) —
  // time enough to actually write the required feedback.
  const [cancelCountdown, setCancelCountdown] = useState(0);
  useEffect(() => {
    if (step !== 'final' || loading) { setCancelCountdown(0); return; }
    setCancelCountdown(10);
    const interval = window.setInterval(() => {
      setCancelCountdown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => window.clearInterval(interval);
  }, [step, loading]);

  // ---- menu choices ------------------------------------------------------
  const goUpgrade = () => {
    onClose();
    navigate('/subscription');
  };

  // Stripe's hosted billing page (change card, billing info, invoices) — a
  // no-cancel portal configuration, so this path can't bypass the funnel.
  const goBilling = async () => {
    if (!userId || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    const result = await apiService
      .cancelFlowBillingPortal(userId, `${window.location.origin}/account`)
      .catch(() => null);
    if (result?.portalUrl) {
      window.location.href = result.portalUrl;
      return;   // keep the spinner while the browser navigates away
    }
    setBusy(false);
    inFlight.current = false;
    toast.error(t('cancelFlow.menuBillingFailed'), 8000);
  };

  const startCancelFlow = () => {
    if (info?.alreadyCanceling) {
      // Nothing to cancel twice — show the existing status instead.
      setStep('done');
      return;
    }
    setStep('reason');
    survey('reason');   // marks funnel entry in v2-CancellationFeedback
  };

  // ---- pause: 1 week billing break --------------------------------------
  const doPause = async () => {
    if (!userId || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    const result = await apiService.cancelFlowPause(userId, flowId).catch(() => null);
    setBusy(false);
    if (!result) { inFlight.current = false; toast.error(t('cancelFlow.pauseFailed')); return; }
    trackAmpRetentionOfferAccepted({
      offer: 'pause',
      user_plan: user?.userPlan || '',
      billing_period: user?.priceType || 'none',
    });
    void refreshUser();
    setResumesAt(result.resumesAt);
    setRetentionKind('pause');
    setStep('retention-done');
  };

  // ---- bonus: +50 credits ------------------------------------------------
  const doBonus = async () => {
    if (!userId || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    const result = await apiService.cancelFlowClaimBonus(userId, flowId).catch(() => null);
    setBusy(false);
    if (!result) { inFlight.current = false; toast.error(t('cancelFlow.bonusFailed')); return; }
    trackAmpRetentionOfferAccepted({
      offer: 'bonus_credits',
      user_plan: user?.userPlan || '',
      billing_period: user?.priceType || 'none',
    });
    void refreshUser();
    setRetentionKind('bonus');
    setStep('retention-done');
  };

  // ---- final cancel ------------------------------------------------------
  const canSubmitCancel = agreed && details.trim().length > 0;
  const confirmCancel = async () => {
    if (!userId || inFlight.current || !canSubmitCancel || !typedOk) return;
    inFlight.current = true;
    setBusy(true);
    let noStripeSub = false;
    const result = await apiService.cancelFlowCancel(userId, {
      flowId,
      reason: reason || 'other',
      details,
      returnUrl: `${window.location.origin}/account`,
    }).catch((err: unknown) => {
      // 409 no_stripe_subscription = they subscribed in the App Store / Play,
      // where only the store itself can cancel. Say so instead of "try again".
      const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      noStripeSub = code === 'no_stripe_subscription';
      return null;
    });

    if (result?.portalUrl) {
      // Stripe's hosted cancel page — the same handoff as the reference flow.
      window.location.href = result.portalUrl;
      return;
    }
    setBusy(false);
    if (result?.canceled) {
      setCanceledUntil(result.periodEnd || info?.periodEnd || null);
      setStep('done');
      void refreshUser();
      return;
    }
    inFlight.current = false;   // failed — let them try again
    toast.error(noStripeSub ? t('cancelFlow.cancelNoSubscription') : t('cancelFlow.cancelFailed'), 8000);
  };

  // ---- formatting --------------------------------------------------------
  const formatDate = (ts?: number | null) => {
    if (!ts) return '';
    return new Date(ts * 1000).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' });
  };
  // The date the pause would end — shown on the pitch before claiming.
  const pausePitchDate = formatDate(Math.floor(Date.now() / 1000) + pauseDays * 86400);

  // ---- footer buttons ----------------------------------------------------
  // min-h (not h) so a wrapped label on narrow screens grows the button
  // instead of overflowing it.
  const btnBase = 'flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-center text-sm font-semibold leading-tight transition-all';
  const btnGhost = cn(btnBase, 'bg-surface-light text-text-primary hover:bg-surface-light/70');
  const btnPrimary = cn(btnBase, 'bg-gradient-to-r from-primary to-accent text-white hover:opacity-90 disabled:opacity-40 disabled:hover:opacity-40');
  const btnDanger = cn(btnBase, 'bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 disabled:hover:bg-red-500');

  const Title = ({ children }: { children: React.ReactNode }) => (
    <h2 className="text-[26px] font-bold leading-tight text-text-primary md:text-[28px]">{children}</h2>
  );
  const Subtitle = ({ children }: { children: React.ReactNode }) => (
    <p className="mt-2.5 text-sm leading-relaxed text-text-secondary">{children}</p>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-text-tertiary" />
        </div>
      );
    }

    // 0 ── Manage subscription hub
    if (step === 'menu') {
      return (
        <>
          <Title>{t('cancelFlow.menuTitle')}</Title>
          <Subtitle>{t('cancelFlow.menuSubtitle')}</Subtitle>

          {/* Plan summary */}
          <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl bg-surface-light px-4 py-3.5">
            <span className="rounded-full bg-gradient-to-r from-primary to-accent px-2.5 py-0.5 text-xs font-bold text-white">{planLabel}</span>
            <span className="flex items-center gap-1.5 text-sm text-text-secondary">
              <Gem className="h-4 w-4 text-primary-light" />
              {t('cancelFlow.menuCredits', { credits })}
            </span>
            {!!info?.periodEnd && !info.alreadyCanceling && (
              <span className="flex items-center gap-1.5 text-sm text-text-secondary">
                <CalendarClock className="h-4 w-4 text-text-tertiary" />
                {t('cancelFlow.menuRenewsOn', { date: formatDate(info.periodEnd) })}
              </span>
            )}
            {!!info?.alreadyCanceling && (
              <span className="flex items-center gap-1.5 text-sm text-amber-400">
                <Info className="h-4 w-4" />
                {t('cancelFlow.menuEndsOn', { date: formatDate(info.periodEnd) })}
              </span>
            )}
          </div>

          {/* The two paths */}
          <div className="mt-4 space-y-3">
            <button
              type="button"
              onClick={goUpgrade}
              className="flex w-full items-center gap-4 rounded-xl border border-glass-border bg-surface-light p-4 text-left transition-all hover:border-primary/40"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                <ArrowUpRight className="h-5 w-5 text-primary-light" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-text-primary">{t('cancelFlow.menuUpgrade')}</p>
                <p className="mt-0.5 text-xs text-text-tertiary">{t('cancelFlow.menuUpgradeDesc')}</p>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-text-tertiary" />
            </button>
            <button
              type="button"
              onClick={() => void goBilling()}
              disabled={busy}
              className="flex w-full items-center gap-4 rounded-xl border border-glass-border bg-surface-light p-4 text-left transition-all hover:border-primary/40 disabled:opacity-60"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                <CreditCard className="h-5 w-5 text-primary-light" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-text-primary">{t('cancelFlow.menuBilling')}</p>
                <p className="mt-0.5 text-xs text-text-tertiary">{t('cancelFlow.menuBillingDesc')}</p>
              </div>
              {busy
                ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-text-tertiary" />
                : <ChevronRight className="h-4 w-4 shrink-0 text-text-tertiary" />}
            </button>
            <button
              type="button"
              onClick={startCancelFlow}
              className="flex w-full items-center gap-4 rounded-xl border border-glass-border bg-surface-light p-4 text-left transition-all hover:border-red-500/30"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-500/10">
                <XCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-text-primary">{t('cancelFlow.menuCancel')}</p>
                <p className="mt-0.5 text-xs text-text-tertiary">{t('cancelFlow.menuCancelDesc')}</p>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-text-tertiary" />
            </button>
          </div>
        </>
      );
    }

    // 1 ── Why are you leaving?
    if (step === 'reason') {
      return (
        <>
          <Title>{t('cancelFlow.reasonTitle')}</Title>
          <Subtitle>{t('cancelFlow.reasonSubtitle')}</Subtitle>
          <div className="mt-7 space-y-0.5">
            {REASONS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReason(r)}
                className="flex w-full items-center gap-3.5 rounded-lg py-2.5 pl-1 pr-2 text-left transition-colors hover:bg-surface-light/60"
              >
                <Radio checked={reason === r} />
                <span className={cn('text-[15px]', reason === r ? 'font-medium text-text-primary' : 'text-text-primary')}>
                  {t(`cancelFlow.reason_${r}`)}
                </span>
              </button>
            ))}
          </div>
        </>
      );
    }

    // 2 ── Learn window: prompting skills + model education + showcase video
    if (step === 'learn') {
      const tips = [
        t('cancelFlow.learnTip1'),
        t('cancelFlow.learnTip2'),
        t('cancelFlow.learnTip3'),
      ];
      const models = [
        { name: 'Gemini Omni Flash', desc: t('cancelFlow.learnModelVeo') },
        { name: 'Sora 2', desc: t('cancelFlow.learnModelSora') },
        { name: 'Kling 3.0', desc: t('cancelFlow.learnModelKling') },
        { name: 'Seedance 2.5', desc: t('cancelFlow.learnModelSeedance') },
      ];
      return (
        <>
          <Title>{t('cancelFlow.learnTitle')}</Title>
          <Subtitle>{t('cancelFlow.learnSubtitle')}</Subtitle>

          <div className="mt-6">
            <VideoShowcase src="/cancel-flow/made-with-vivideo.mp4" badge={t('cancelFlow.learnVideoBadge')} />
          </div>

          <div className="mt-6 rounded-2xl bg-surface-light p-4">
            <p className="flex items-center gap-2 text-sm font-bold text-text-primary">
              <Lightbulb className="h-4 w-4 shrink-0 text-amber-400" />
              {t('cancelFlow.learnPromptHeading')}
            </p>
            <div className="mt-3 space-y-2.5">
              {tips.map((tip) => (
                <div key={tip} className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                  <span className="text-sm text-text-secondary">{tip}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 rounded-2xl bg-surface-light p-4">
            <p className="flex items-center gap-2 text-sm font-bold text-text-primary">
              <Wand2 className="h-4 w-4 shrink-0 text-primary-light" />
              {t('cancelFlow.learnModelsHeading')}
            </p>
            <div className="mt-3 space-y-2">
              {models.map((m) => (
                <div key={m.name} className="flex items-baseline gap-2.5">
                  <span className="w-28 shrink-0 text-sm font-semibold text-text-primary">{m.name}</span>
                  <span className="text-sm text-text-secondary">{m.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      );
    }

    // 3 ── Are you sure?
    if (step === 'confirm') {
      const rows = [
        {
          icon: Gem,
          title: t('cancelFlow.assetCreditsTitle', { credits }),
          sub: t('cancelFlow.assetCreditsSub', { videos: Math.floor(credits / creditsPerVideo) }),
        },
        {
          icon: FolderOpen,
          title: t('cancelFlow.assetProjectsTitle'),
          // Real counts when the snapshot loaded; a generic line otherwise —
          // never a false "0 projects, 0 avatars…".
          sub: info
            ? t('cancelFlow.assetProjectsSub', {
                videos: info.assets.videos,
                avatars: info.assets.avatars,
                voices: info.assets.voices,
                files: info.assets.files,
              })
            : t('cancelFlow.assetProjectsSubGeneric'),
        },
        {
          icon: Wand2,
          title: t('cancelFlow.assetModelsTitle'),
          sub: t('cancelFlow.assetModelsSub'),
        },
      ];
      return (
        <>
          <Title>{t('cancelFlow.confirmTitle')}</Title>
          <Subtitle>{t('cancelFlow.confirmSubtitle')}</Subtitle>

          {!!info?.daysLeft && (
            <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-amber-500/25 bg-amber-500/10 px-3.5 py-3">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
              <p className="text-sm text-amber-200/90">
                {t('cancelFlow.daysLeft', { days: info.daysLeft, plan: planLabel })}
              </p>
            </div>
          )}

          <div className="mt-4 overflow-hidden rounded-2xl bg-surface-light">
            <p className="px-4 pb-3 pt-4 text-sm font-semibold text-text-primary">
              {t('cancelFlow.currentPlanHeading', { plan: planLabel })}
            </p>
            <div className="space-y-4 bg-black/10 px-4 py-4">
              {rows.map((row) => (
                <div key={row.title} className="flex items-start gap-3">
                  <row.icon className="mt-0.5 h-5 w-5 shrink-0 text-primary-light" />
                  <div>
                    <p className="text-sm font-semibold text-text-primary">{row.title}</p>
                    <p className="mt-0.5 text-xs text-text-tertiary">{row.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      );
    }

    // 3 ── Pause for 1 week instead
    if (step === 'pause') {
      const perks = [
        t('cancelFlow.pausePerkNoCharge'),
        t('cancelFlow.pausePerkAutoResume', { date: pausePitchDate }),
        t('cancelFlow.pausePerkCreditsBack'),
      ];
      return (
        <>
          <Title>{t('cancelFlow.pauseTitle')}</Title>
          <Subtitle>{t('cancelFlow.pauseSubtitle', { days: pauseDays })}</Subtitle>

          <div className="mt-6 rounded-2xl bg-surface-light p-5">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/15">
                <Pause className="h-7 w-7 text-primary-light" fill="currentColor" />
              </div>
              <div>
                <p className="text-lg font-bold text-text-primary">{t('cancelFlow.pauseCardTitle')}</p>
                <p className="text-xs text-text-tertiary">{t('cancelFlow.pauseCardSub', { plan: planLabel })}</p>
              </div>
            </div>
            <div className="my-4 border-t border-glass-border" />
            <div className="space-y-2.5">
              {perks.map((perk) => (
                <div key={perk} className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                  <span className="text-sm text-text-secondary">{perk}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Rejecting the pause arms this acknowledgement — amber attention,
              must be ticked before the (now red) continue button works. */}
          {pauseRejectArmed && (
            <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3.5 py-3 animate-fade-in">
              <span
                className={cn(
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors',
                  pauseRejectAgreed ? 'border-amber-400 bg-amber-400' : 'border-amber-400/70'
                )}
              >
                {pauseRejectAgreed && <Check className="h-3.5 w-3.5 text-black" />}
              </span>
              <input
                type="checkbox"
                checked={pauseRejectAgreed}
                onChange={(e) => setPauseRejectAgreed(e.target.checked)}
                className="sr-only"
              />
              <span className="text-sm text-amber-200/90">{t('cancelFlow.pauseRejectAgree')}</span>
            </label>
          )}
        </>
      );
    }

    // 4 ── Use your credits first (advisory — shown while credits remain)
    if (step === 'use-credits') {
      return (
        <>
          <Title>{t('cancelFlow.useCreditsTitle')}</Title>
          <Subtitle>{t('cancelFlow.useCreditsSubtitle')}</Subtitle>
          <div className="mt-7 flex h-52 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-amber-500/60 bg-gradient-to-br from-amber-500/15 via-surface-light to-primary/10">
            <p className="text-5xl font-bold text-text-primary">{credits}</p>
            <p className="mt-2 text-sm font-medium text-text-secondary">{t('cancelFlow.useCreditsCardLabel')}</p>
            <p className="mt-1 text-xs text-text-tertiary">{t('cancelFlow.useCreditsCardSub', { videos: Math.floor(credits / creditsPerVideo) })}</p>
          </div>

          {/* Armed by the first continue press: the exact dollars they torch. */}
          {wasteArmed && (
            <div className="mt-4 rounded-xl border-2 border-red-500/60 bg-red-500/10 p-4 animate-fade-in">
              <p className="flex items-center gap-2 text-sm font-bold text-red-400">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {t('cancelFlow.wasteTitle')}
              </p>
              <div className="mt-3 space-y-1.5 text-sm text-text-secondary">
                <p>{t('cancelFlow.wasteCredits', { value: wasteCreditsValue.toFixed(2) })}</p>
                {wasteDaysValue > 0 && <p>{t('cancelFlow.wasteDays', { value: wasteDaysValue.toFixed(2) })}</p>}
              </div>
              <p className="mt-3 animate-pulse text-2xl font-bold text-red-400">
                {t('cancelFlow.wasteTotal', { total: wasteTotal.toFixed(2) })}
              </p>
            </div>
          )}
        </>
      );
    }

    // 5 ── Want bonus credits?
    if (step === 'bonus') {
      return (
        <>
          <Title>{t('cancelFlow.bonusTitle', { credits: bonusCredits })}</Title>
          <Subtitle>{t('cancelFlow.bonusSubtitle', { credits: bonusCredits })}</Subtitle>
          <div className="mt-7 flex h-52 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-primary/60 bg-gradient-to-br from-primary/20 via-surface-light to-accent/15">
            <p className="text-sm font-medium text-text-secondary">{t('cancelFlow.bonusCardLabel')}</p>
            <p className="mt-2 text-5xl font-bold text-text-primary">+{bonusCredits}</p>
            <p className="mt-2 text-xs text-text-tertiary">{t('cancelFlow.bonusCardSub', { videos: Math.floor(bonusCredits / creditsPerVideo) })}</p>
          </div>
        </>
      );
    }

    // 5 ── Final step (textarea REQUIRED + checkbox REQUIRED)
    if (step === 'final') {
      return (
        // Column layout so the acknowledgement sits just above the footer
        // buttons, as in the reference — not floating under the textarea.
        <div className="flex h-full flex-col">
          <Title>{t('cancelFlow.finalTitle')}</Title>
          <Subtitle>{t(`cancelFlow.finalSubtitle_${reason || 'other'}`)}</Subtitle>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder={t('cancelFlow.finalPlaceholder')}
            maxLength={2000}
            className="mt-5 h-48 w-full resize-none rounded-xl border border-glass-border bg-surface-light px-4 py-3 text-sm text-text-primary outline-none transition-colors placeholder:text-text-tertiary focus:border-accent"
          />
          {/* The visual box is decorative — the hidden input owns the state so
              the surrounding <label> toggles it exactly once per click. */}
          <label className="mt-auto flex cursor-pointer items-start gap-3 pt-6">
            <span
              className={cn(
                'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors',
                agreed ? 'border-accent bg-accent' : 'border-text-tertiary'
              )}
            >
              {agreed && <Check className="h-3.5 w-3.5 text-white" />}
            </span>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="sr-only"
            />
            <span className="text-sm text-text-secondary">{t('cancelFlow.finalAgree')}</span>
          </label>
        </div>
      );
    }

    // 7 ── Type-to-confirm (the very last gate before Stripe)
    if (step === 'type-confirm') {
      return (
        <div className="flex h-full flex-col">
          <Title>{t('cancelFlow.typeConfirmTitle')}</Title>
          <Subtitle>{t('cancelFlow.typeConfirmSubtitle', { word: confirmWord })}</Subtitle>
          <label className="mt-8 block text-sm text-text-secondary">
            {t('cancelFlow.typeConfirmLabel', { word: confirmWord })}
          </label>
          <input
            type="text"
            value={typedConfirm}
            onChange={(e) => setTypedConfirm(e.target.value)}
            placeholder={confirmWord}
            autoComplete="off"
            autoCapitalize="characters"
            className="mt-2 w-full rounded-xl border border-glass-border bg-surface-light px-4 py-3.5 text-lg font-bold tracking-[0.3em] text-text-primary outline-none transition-colors placeholder:text-text-tertiary placeholder:tracking-[0.3em] focus:border-red-500/50"
          />
          <p className="mt-auto pt-6 text-xs text-text-tertiary">{t('cancelFlow.typeConfirmHint')}</p>
        </div>
      );
    }

    // ── Retention success (pause / bonus claimed)
    if (step === 'retention-done') {
      return (
        <div className="flex h-full flex-col items-center justify-center text-center">
          <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Check className="h-8 w-8 text-primary" />
          </div>
          <Title>
            {retentionKind === 'pause'
              ? t('cancelFlow.pausedTitle')
              : t('cancelFlow.bonusDoneTitle', { credits: bonusCredits })}
          </Title>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-text-secondary">
            {retentionKind === 'pause'
              ? t('cancelFlow.pausedDesc', { date: formatDate(resumesAt) })
              : t('cancelFlow.bonusDoneDesc')}
          </p>
        </div>
      );
    }

    // 6 ── Cancelled (only when Stripe's hosted page is unavailable)
    if (step === 'done') {
      return (
        <div className="flex h-full flex-col items-center justify-center text-center">
          <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Check className="h-8 w-8 text-primary" />
          </div>
          <Title>{t('cancelFlow.doneTitle')}</Title>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-text-secondary">
            {canceledUntil
              ? t('cancelFlow.doneUntil', { plan: planLabel, date: formatDate(canceledUntil) })
              : t('cancelFlow.doneGeneric', { plan: planLabel })}
          </p>
        </div>
      );
    }

    return null;
  };

  const renderFooter = () => {
    if (loading) return null;
    if (step === 'menu') {
      return <button onClick={onClose} className={btnGhost}>{t('cancelFlow.close')}</button>;
    }
    if (step === 'done' || step === 'retention-done') {
      return <button onClick={onClose} className={btnPrimary}>{t('cancelFlow.done')}</button>;
    }
    if (step === 'reason') {
      return (
        <>
          <button onClick={() => keepPlan('kept_reason')} className={btnPrimary}>{t('cancelFlow.neverMind')}</button>
          {scrolledDown && (
            <button onClick={() => goNext('reason')} disabled={!reason || !gateDone} className={cn(btnGhost, 'disabled:opacity-40')}>{t('cancelFlow.continue')}</button>
          )}
        </>
      );
    }
    if (step === 'learn') {
      return (
        <>
          {scrolledDown && (
            <button onClick={() => goNext('learn')} disabled={!gateDone} className={cn(btnGhost, 'disabled:opacity-40')}>{t('cancelFlow.continueCancellation')}</button>
          )}
          <button onClick={() => keepPlan('kept_learn')} className={btnPrimary}>{t('cancelFlow.learnStay')}</button>
        </>
      );
    }
    if (step === 'confirm') {
      return (
        <>
          {scrolledDown && (
            <button onClick={() => goNext('confirm')} disabled={!gateDone} className={cn(btnGhost, 'disabled:opacity-40')}>{t('cancelFlow.continueCancellation')}</button>
          )}
          <button onClick={() => keepPlan('kept_confirm')} className={btnPrimary}>{t('cancelFlow.keepMyPlan')}</button>
        </>
      );
    }
    if (step === 'pause') {
      return (
        <>
          {scrolledDown && (
            <button
              onClick={() => {
                // First press = reject the pause: arm the PERMANENTLY-deleted
                // acknowledgement instead of advancing. Second press (red,
                // checkbox ticked) actually continues.
                if (!pauseRejectArmed) { setPauseRejectArmed(true); return; }
                goNext('pause');
              }}
              disabled={!gateDone || (pauseRejectArmed && !pauseRejectAgreed)}
              className={pauseRejectArmed ? btnDanger : cn(btnGhost, 'disabled:opacity-40')}
            >
              {t('cancelFlow.continueCancellation')}
            </button>
          )}
          <button onClick={() => void doPause()} disabled={busy} className={btnPrimary}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('cancelFlow.pauseCta')}
          </button>
        </>
      );
    }
    if (step === 'use-credits') {
      return (
        <>
          {scrolledDown && (
            <button
              onClick={() => {
                // First press = arm the money-wasted warning; second proceeds.
                if (!wasteArmed) { setWasteArmed(true); return; }
                goNext('use-credits');
              }}
              disabled={!gateDone}
              className={wasteArmed ? btnDanger : cn(btnGhost, 'disabled:opacity-40')}
            >
              {t('cancelFlow.continueCancellation')}
            </button>
          )}
          <button onClick={() => keepPlan('kept_use_credits')} className={btnPrimary}>{t('cancelFlow.useCreditsStay')}</button>
        </>
      );
    }
    if (step === 'bonus') {
      return (
        <>
          {scrolledDown && (
            <button onClick={() => goNext('bonus')} disabled={!gateDone} className={cn(btnGhost, 'disabled:opacity-40')}>{t('cancelFlow.continueCancellation')}</button>
          )}
          <button onClick={() => void doBonus()} disabled={busy} className={btnPrimary}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('cancelFlow.bonusCta', { credits: bonusCredits })}
          </button>
        </>
      );
    }
    if (step === 'final') {
      return (
        <>
          <button onClick={() => keepPlan('kept_final')} className={btnPrimary}>{t('cancelFlow.keepMyPlan')}</button>
          {scrolledDown && (
            <button onClick={() => goNext('final')} disabled={!canSubmitCancel || cancelCountdown > 0} className={btnDanger}>
              {cancelCountdown > 0
                ? t('cancelFlow.cancelCountdown', { seconds: cancelCountdown })
                : t('cancelFlow.cancelSubscription')}
            </button>
          )}
        </>
      );
    }
    // type-confirm — the REAL cancel button, unlocked only by the typed word.
    return (
      <>
        <button onClick={() => keepPlan('kept_type_confirm')} className={btnPrimary}>{t('cancelFlow.keepMyPlan')}</button>
        {scrolledDown && (
          <button onClick={() => void confirmCancel()} disabled={!typedOk || busy} className={btnDanger}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('cancelFlow.cancelSubscription')}
          </button>
        )}
      </>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-6 md:px-14 animate-fade-in">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />

      <div className="relative z-10 w-full max-w-[1000px]">
        <button
          onClick={onClose}
          aria-label={t('cancelFlow.close')}
          className="absolute -right-2 -top-2 z-20 rounded-full bg-surface-light p-2 text-text-secondary shadow-lg transition-colors hover:text-text-primary md:-right-12 md:top-0"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex h-[min(700px,88vh)] w-full overflow-hidden rounded-2xl border border-glass-border bg-surface shadow-2xl">
          <div className="flex min-w-0 flex-1 flex-col p-6 md:p-8">
            <div className="relative min-h-0 flex-1">
              <div ref={bodyRef} onScroll={onBodyScroll} className="h-full overflow-y-auto pr-1">
                <div ref={contentRef}>{renderBody()}</div>
                {isScrollGated && spacerPx > 0 && <div style={{ height: spacerPx }} aria-hidden="true" />}
              </div>
              {/* Small arrow: there is more below — scroll to reveal the
                  continue button. Disappears once the bottom is reached. */}
              {isScrollGated && !scrolledDown && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-surface via-surface/80 to-transparent pb-0.5 pt-8">
                  <ChevronDown className="h-5 w-5 animate-bounce text-text-secondary" />
                </div>
              )}
            </div>
            <div className="mt-6 shrink-0">
              {/* 5s reading gate: fills once per window, then stays full as the
                  "you may proceed" affordance (no layout jump). */}
              {isGatedStep && !loading && (
                <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-surface-light" aria-hidden="true">
                  <div key={step} className="h-full w-full origin-left animate-gate-fill bg-gradient-to-r from-primary to-accent" />
                </div>
              )}
              <div className="flex gap-3">{renderFooter()}</div>
            </div>
          </div>
          <ArtPanel step={step} credits={credits} bonusCredits={bonusCredits} />
        </div>
      </div>
    </div>,
    document.body
  );
}
